import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useState } from 'react';

/**
 * Hook to prevent flickering/jumping during @dnd-kit reordering operations, even
 * when using optimistic updates in external state stores like React Query.
 *
 * The root cause is that @dnd-kit expects component updates in the same batched
 * state updates as onDragEnd, but React Query's cache updates happen through
 * internal observers that may not be in the same render cycle.
 *
 * Alternative solutions include:
 * - Using DragOverlay (which coincidentally fixes the issue by managing active state)
 *
 * For more details, see: https://github.com/clauderic/dnd-kit/issues/921
 * Implementation based on: https://github.com/clauderic/dnd-kit/discussions/1522#discussioncomment-13526147
 */

interface EntryBase {
	id: string;
}

export const useDragReorder = <Entry extends EntryBase>(
	entries: Entry[],
	mutateOptimistic: (newOrder: string[]) => Promise<string[]>
) => {
	const [tempEntries, setTempEntries] = useState<Entry[] | null>(null);

	const reorder = useCallback(
		async (event: DragEndEvent) => {
			const { active, over } = event;

			if (!over || active.id === over.id) {
				return;
			}

			const oldIndex = entries.findIndex((entry) => entry.id === active.id);
			const newIndex = entries.findIndex((entry) => entry.id === over.id);

			if (oldIndex === -1 || newIndex === -1) {
				return;
			}

			const reordered = arrayMove(entries, oldIndex, newIndex);
			const newOrder = reordered.map((entry) => entry.id);

			setTempEntries(reordered);
			await mutateOptimistic(newOrder);
			setTempEntries(null);
		},
		[mutateOptimistic, entries]
	);

	return {
		reorder,
		items: tempEntries ?? entries,
	};
};
